"use client";

import { isAutoSubmitWidget, resolveToolName } from "@health/types/ui-tools";
import { WidgetCard } from "@health/ui";
import { SkipForward } from "lucide-react";
import { type ComponentType, lazy, Suspense } from "react";
import { ChipGroupWidget } from "./ChipGroupWidget";
import { ChipsWidget } from "./ChipsWidget";
import { ClaimWidget } from "./ClaimWidget";
import { InlineTextInput } from "./InlineTextInput";
import { ProgressBar } from "./ProgressBar";
import { WidgetShell } from "./WidgetShell";

// Eager widgets stay as static imports above — they're on the first-paint
// critical path (chips + text input + progress + claim) and shipping them
// lazily would cost a Suspense flash on the first chip interaction.
//
// Everything else below is wrapped in React.lazy() with a module-scoped
// loader callback. Naming pattern: `loadFoo` returns the same Promise
// the `lazy(...)` closure uses, so the preload hook can warm the chunk
// before React actually mounts the component.
//
// IMPORTANT (React 19 + Vite): preload by calling `loadFoo()` directly,
// not by writing a bare `import()` elsewhere. Browser module-dedup means
// a separately-written `import()` call and the one inside `lazy()` resolve
// to different Promise instances until the network fetch completes, which
// can still trip a Suspense flash on slow connections.

const loadAuthWidget = () => import("./AuthWidget");
const LazyAuthWidget = lazy(() => loadAuthWidget().then((m) => ({ default: m.AuthWidget })));

const loadBMIResultWidget = () => import("./BMIResultWidget");
const LazyBMIResultWidget = lazy(() =>
  loadBMIResultWidget().then((m) => ({ default: m.BMIResultWidget })),
);

const loadComparison = () => import("./Comparison");
const LazyComparison = lazy(() => loadComparison().then((m) => ({ default: m.Comparison })));

const loadIntakeSummary = () => import("./IntakeSummary");
const LazyIntakeSummary = lazy(() =>
  loadIntakeSummary().then((m) => ({ default: m.IntakeSummary })),
);

const loadJsonForm = () => import("./JsonForm");
const LazyJsonForm = lazy(() => loadJsonForm().then((m) => ({ default: m.JsonForm })));

const loadMeducationWidget = () => import("./MeducationWidget");
const LazyMeducationWidget = lazy(() =>
  loadMeducationWidget().then((m) => ({ default: m.MeducationWidget })),
);

const loadPatientProfile = () => import("./PatientProfile");
const LazyPatientProfile = lazy(() =>
  loadPatientProfile().then((m) => ({ default: m.PatientProfile })),
);

const loadPricingBreakdown = () => import("./PricingBreakdown");
const LazyPricingBreakdown = lazy(() =>
  loadPricingBreakdown().then((m) => ({ default: m.PricingBreakdown })),
);

const loadProductCard = () => import("./ProductCard");
const LazyProductCard = lazy(() => loadProductCard().then((m) => ({ default: m.ProductCard })));

const loadMeducationHeroWidget = () => import("./MeducationHeroWidget");
const LazyMeducationHeroWidget = lazy(() =>
  loadMeducationHeroWidget().then((m) => ({ default: m.MeducationHeroWidget })),
);

/**
 * Renders MeducationHeroWidget when the server has resolved hero content for
 * the product, otherwise falls back to the standard ProductCard.
 * This keeps the registry entry for show_product as a single key while
 * transparently upgrading the UI for treatments with registered hero content.
 */
function ProductOrHeroWidget(props: {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  demoMode?: boolean;
  streaming?: boolean;
  historic?: boolean;
}) {
  const hasHeroContent =
    props.output !== undefined &&
    typeof props.output === "object" &&
    props.output !== null &&
    "heroContent" in props.output &&
    (props.output as Record<string, unknown>).heroContent !== undefined;

  if (hasHeroContent) {
    return <LazyMeducationHeroWidget {...props} />;
  }
  return <LazyProductCard {...props} />;
}

const loadReasoningTrace = () => import("./ReasoningTrace");
const LazyReasoningTrace = lazy(() =>
  loadReasoningTrace().then((m) => ({ default: m.ReasoningTrace })),
);

const loadStructuredInputWidget = () => import("./StructuredInputWidget");
const LazyStructuredInputWidget = lazy(() =>
  loadStructuredInputWidget().then((m) => ({ default: m.StructuredInputWidget })),
);

const loadTelehealthConsentWidget = () => import("./TelehealthConsentWidget");
const LazyTelehealthConsentWidget = lazy(() =>
  loadTelehealthConsentWidget().then((m) => ({ default: m.TelehealthConsentWidget })),
);

const loadTestimonialWidget = () => import("./TestimonialWidget");
const LazyTestimonialWidget = lazy(() =>
  loadTestimonialWidget().then((m) => ({ default: m.TestimonialWidget })),
);

const loadUnifiedCheckout = () => import("./checkout");
const LazyUnifiedCheckout = lazy(() =>
  loadUnifiedCheckout().then((m) => ({ default: m.UnifiedCheckout })),
);

const loadWeightInputWidget = () => import("./WeightInputWidget");
const LazyWeightInputWidget = lazy(() =>
  loadWeightInputWidget().then((m) => ({ default: m.WeightInputWidget })),
);

/**
 * Maps tool name -> underlying chunk loader for lazy widgets. Exposed via
 * {@link preloadWidgetChunk} so callers (e.g. ChatPanel) can warm a chunk
 * on `tool-input-start` — before React Suspends.
 *
 * Keys must match the canonical tool names used in {@link WidgetRegistry}
 * below and {@link WidgetLoadingRegistry}.
 */
const LAZY_LOADERS: Record<string, () => Promise<unknown>> = {
  show_auth: loadAuthWidget,
  show_bmi: loadBMIResultWidget,
  show_checkout: loadUnifiedCheckout,
  show_comparison: loadComparison,
  show_consent: loadTelehealthConsentWidget,
  show_form: loadJsonForm,
  show_intake_summary: loadIntakeSummary,
  show_meducation: loadMeducationWidget,
  show_patient_profile: loadPatientProfile,
  show_pricing: loadPricingBreakdown,
  show_product: () => Promise.all([loadProductCard(), loadMeducationHeroWidget()]),
  show_reasoning_trace: loadReasoningTrace,
  show_structured_input: loadStructuredInputWidget,
  show_testimonial: loadTestimonialWidget,
  show_weight_input: loadWeightInputWidget,
};

/**
 * Warm the JS chunk for a lazy widget before it mounts. Safe to call on
 * every `tool-input-start` stream event — the loader is idempotent (browser
 * module cache dedups subsequent imports).
 *
 * Eager widgets (chips, text input, progress, claim) are no-ops here —
 * they're already in the main bundle.
 */
export function preloadWidgetChunk(toolName: string): void {
  const canonical = String(resolveToolName(toolName));
  const loader = LAZY_LOADERS[canonical];
  if (!loader) return;
  // Kick off the fetch; ignore result and errors. The loader's resolved
  // module will be served from the browser module cache when the `lazy()`
  // closure eventually resolves it, so Suspense does not flash.
  loader().catch(() => {});
}

type Props = {
  toolName: string;
  toolCallId: string;
  input: Record<string, unknown>;
  output?: unknown;
  addToolOutput: (options: { tool: string; toolCallId: string; output: unknown }) => Promise<void>;
  submitted?: boolean;
  demoMode?: boolean;
  streaming?: boolean;
  /** True when a user-role message appears later in the feed. */
  historic?: boolean;
};

const registry: Record<
  string,
  ComponentType<{
    input: Record<string, unknown>;
    output?: unknown;
    onSubmit: (result: unknown) => void;
    submitted?: boolean;
    demoMode?: boolean;
    streaming?: boolean;
    historic?: boolean;
  }>
> = {
  show_form: LazyJsonForm,
  show_product: ProductOrHeroWidget,
  show_progress: ProgressBar,
  show_auth: LazyAuthWidget,
  show_claim: ClaimWidget,
  show_testimonial: LazyTestimonialWidget,
  show_pricing: LazyPricingBreakdown,
  show_comparison: LazyComparison,
  show_bmi: LazyBMIResultWidget,
  show_checkout: LazyUnifiedCheckout,
  show_intake_summary: LazyIntakeSummary,
  show_chips: ChipsWidget,
  show_chip_group: ChipGroupWidget,
  show_consent: LazyTelehealthConsentWidget,
  show_weight_input: LazyWeightInputWidget,
  show_reasoning_trace: LazyReasoningTrace,
  show_patient_profile: LazyPatientProfile,
  show_text_input: InlineTextInput,
  show_structured_input: LazyStructuredInputWidget,
  show_meducation: LazyMeducationWidget,
};

/** Auto-submit widgets render in full even in history — no special treatment. */
/** Widget labels for skipped state */
const widgetLabels: Record<string, string> = {
  show_product: "Product recommendation",
  show_comparison: "Product comparison",
  show_pricing: "Pricing breakdown",
  show_auth: "Save progress",
  show_meducation: "Treatment education",
};

/**
 * Derive a short human-readable label for the widget's question/context
 * from its input props, used in the skipped state.
 */
function widgetLabel(toolName: string, input: Record<string, unknown>): string {
  // JSON form — use schema title
  if (toolName === "show_form") {
    const schema = input.schema as Record<string, unknown> | undefined;
    if (schema?.title) return schema.title as string;
  }

  // Use predefined label or humanize tool name
  return widgetLabels[toolName] || toolName.replace("show_", "").replace(/_/g, " ");
}

/**
 * Check if JsonForm should have a wrapper div.
 * Returns true if the form renders multiple elements (question + answer) needing spacing.
 */
function shouldWrapJsonForm(input: Record<string, unknown>, output: unknown): boolean {
  const schema = input.schema as
    | { title?: string; properties?: Record<string, unknown> }
    | undefined;
  const formData = (output as { formData?: Record<string, unknown> })?.formData;
  const fieldCount = formData ? Object.keys(formData).length : 0;

  // Check if single field has a title
  if (fieldCount === 1 && formData && schema?.properties) {
    const fieldKey = Object.keys(formData)[0];
    const propertySchema = schema.properties[fieldKey] as { title?: string } | undefined;
    const hasFieldTitle = !!propertySchema?.title;

    // Single field with any title needs wrapper (renders Q&A pair)
    if (schema?.title || hasFieldTitle) return true;

    // Single field without title doesn't need wrapper (renders just UserMessage)
    return false;
  }

  // Multiple fields always need wrapper
  return true;
}

/** Renders a skipped widget placeholder */
function SkippedWidget({ label }: { label: string }) {
  return (
    <div className="w-full opacity-40">
      <WidgetCard
        $padding="none"
        className="px-3 py-2 flex items-center gap-2 text-sm text-text-secondary italic"
      >
        <SkipForward size={14} />
        <span>{label} — skipped</span>
      </WidgetCard>
    </div>
  );
}

export function WidgetRegistry({
  toolName,
  toolCallId,
  input,
  output,
  addToolOutput,
  submitted,
  demoMode,
  streaming,
  historic,
}: Props) {
  const canonicalToolName = resolveToolName(toolName);
  const Widget = registry[canonicalToolName];
  if (!Widget) {
    return null;
  }

  const handleSubmit = (result: unknown) => {
    addToolOutput({
      tool: canonicalToolName,
      toolCallId,
      output: result,
    });
  };

  const shellState = submitted ? "submitted" : "pending";

  // If the widget was auto-skipped (user sent a new message), show a collapsed state
  if (
    submitted &&
    output &&
    typeof output === "object" &&
    (output as Record<string, unknown>).skipped
  ) {
    return <SkippedWidget label={widgetLabel(canonicalToolName, input)} />;
  }

  // If the widget was cancelled (user closed the sheet), show a collapsed state
  if (
    submitted &&
    output &&
    typeof output === "object" &&
    (output as Record<string, unknown>).action === "cancelled"
  ) {
    return <SkippedWidget label={widgetLabel(canonicalToolName, input)} />;
  }

  // Historical widget the user never interacted with — gray out
  if (submitted === false) {
    return <SkippedWidget label={widgetLabel(canonicalToolName, input)} />;
  }

  // Historical widget with output — show completed state
  if (submitted && output && !isAutoSubmitWidget(canonicalToolName)) {
    // JsonForm handles its own alignment (returns UserMessage or AgentMessage+UserMessage)
    if (canonicalToolName === "show_form") {
      const needsWrapper = shouldWrapJsonForm(input, output);

      if (needsWrapper) {
        // With title OR multiple fields: JsonForm returns fragment needing wrapper
        return (
          <WidgetShell
            toolName={canonicalToolName}
            toolCallId={toolCallId}
            input={input}
            state={shellState}
          >
            <div className="flex flex-col gap-3 w-full">
              <Suspense fallback={null}>
                <Widget
                  input={input}
                  output={output}
                  onSubmit={handleSubmit}
                  submitted={submitted}
                  demoMode={demoMode}
                  historic={historic}
                />
              </Suspense>
            </div>
          </WidgetShell>
        );
      }

      // Single field without any title: JsonForm returns just UserMessage
      return (
        <WidgetShell
          toolName={canonicalToolName}
          toolCallId={toolCallId}
          input={input}
          state={shellState}
        >
          <Suspense fallback={null}>
            <Widget
              input={input}
              output={output}
              onSubmit={handleSubmit}
              submitted={submitted}
              demoMode={demoMode}
              historic={historic}
            />
          </Suspense>
        </WidgetShell>
      );
    }

    return (
      <WidgetShell
        toolName={canonicalToolName}
        toolCallId={toolCallId}
        input={input}
        state={shellState}
        className="w-full"
      >
        <div>
          <Suspense fallback={null}>
            <Widget
              input={input}
              output={output}
              onSubmit={handleSubmit}
              submitted={submitted}
              demoMode={demoMode}
              historic={historic}
            />
          </Suspense>
        </div>
      </WidgetShell>
    );
  }

  return (
    <WidgetShell
      toolName={canonicalToolName}
      input={input}
      state={shellState}
      className={streaming ? "w-full" : "w-full animate-fade-in"}
    >
      <Suspense fallback={null}>
        <Widget
          input={input}
          output={output}
          onSubmit={handleSubmit}
          submitted={submitted}
          demoMode={demoMode}
          streaming={streaming}
          historic={historic}
        />
      </Suspense>
    </WidgetShell>
  );
}
